/**
 * Deterministic PRNG. Integer-only (mulberry32), so results are identical
 * in V8 (Android Telegram, Node) and JavaScriptCore (iOS Telegram).
 * Never use Math.random inside the simulation.
 */
export interface RngState {
  a: number;
}

/** FNV-1a 32-bit hash: turns a string seed (e.g. "2026-09-23:map01") into an int seed. */
export function seedFromString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export function createRng(seed: number): RngState {
  return { a: seed | 0 };
}

export function nextU32(r: RngState): number {
  r.a = (r.a + 0x6d2b79f5) | 0;
  let t = Math.imul(r.a ^ (r.a >>> 15), 1 | r.a);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return (t ^ (t >>> 14)) >>> 0;
}

/** Uniform-ish integer in [0, n). n must be > 0. */
export function nextInt(r: RngState, n: number): number {
  if (n <= 0) throw new Error(`nextInt: n must be > 0, got ${n}`);
  return nextU32(r) % n;
}

export function pickWeighted<T>(r: RngState, items: ReadonlyArray<{ item: T; w: number }>): T {
  let total = 0;
  for (const it of items) total += it.w;
  let roll = nextInt(r, total);
  for (const it of items) {
    if (roll < it.w) return it.item;
    roll -= it.w;
  }
  // Unreachable when weights are positive; keep the type checker happy.
  return items[items.length - 1]!.item;
}

/** In-place Fisher–Yates shuffle, deterministic. */
export function shuffle<T>(r: RngState, arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = nextInt(r, i + 1);
    const tmp = arr[i]!;
    arr[i] = arr[j]!;
    arr[j] = tmp;
  }
  return arr;
}
